import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaSync, FaTimes, FaPlus, FaEdit } from "react-icons/fa";
import PageHeader from "../../core/common/header/PageHeader";

interface OutlineStep {
  stepOrder: number;
  title: string;
  description: string;
}
interface PriceOption {
  level: string;
  amount: number;
}

interface CourseDto {
  id: number;
  title: string;
  description: string;
  regulations: string;
  imageUrl: string;
  price: number;
  installmentCount?: number;
  paymentInfo?: string;
  startDate?: string;
  registrationDeadline?: string;
  priceOptions: PriceOption[];
  outlineSteps: OutlineStep[];
}

const EditCourse: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [course, setCourse] = useState<CourseDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCourse = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/courses/${id}`,
        {
          credentials: "include",
        }
      );
      if (!response.ok) throw new Error(`Load failed (${response.status})`);
      const data = await response.json();
      setCourse(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourse();
  }, [id]);

  const updateField = <K extends keyof CourseDto>(key: K, val: CourseDto[K]) =>
    setCourse((c) => (c ? { ...c, [key]: val } : c));

  const updateStep = (i: number, data: Partial<OutlineStep>) => {
    if (!course) return;
    const steps = course.outlineSteps.map((s, idx) =>
      idx === i ? { ...s, ...data } : s
    );
    setCourse({ ...course, outlineSteps: steps });
  };
  const removeStep = (i: number) => {
    if (!course) return;
    setCourse({
      ...course,
      outlineSteps: course.outlineSteps.filter((_, idx) => idx !== i),
    });
  };
  const addStep = () => {
    if (!course) return;
    setCourse({
      ...course,
      outlineSteps: [
        ...course.outlineSteps,
        {
          stepOrder: course.outlineSteps.length + 1,
          title: "",
          description: "",
        },
      ],
    });
  };

  const updatePriceOpt = (i: number, data: Partial<PriceOption>) => {
    if (!course) return;
    const opts = course.priceOptions.map((o, idx) =>
      idx === i ? { ...o, ...data } : o
    );
    setCourse({ ...course, priceOptions: opts });
  };
  const removePriceOpt = (i: number) => {
    if (!course) return;
    setCourse({
      ...course,
      priceOptions: course.priceOptions.filter((_, idx) => idx !== i),
    });
  };
  const addPriceOpt = () => {
    if (!course) return;
    setCourse({
      ...course,
      priceOptions: [...course.priceOptions, { level: "", amount: 0 }],
    });
  };

  const handleSubmit = async () => {
    if (!course) return;
    setError(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/courses/${id}`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(course),
        }
      );
      if (!res.ok) throw new Error((await res.text()) || `Error ${res.status}`);
      navigate("/admin/manage-courses");
    } catch (e: any) {
      setError(e.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <FaSync className="animate-spin h-12 w-12 text-[#009fe3] mx-auto mb-4" />
          <p className="text-gray-600">Loading course details...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Failed to load course</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaEdit />}
        title={`Edit Course #${id}`}
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Manage Courses", to: "/admin/manage-courses" },
          { label: "Edit Course" },
        ]}
      />

      <div className="flex justify-end gap-2 max-w-3xl mx-auto px-4 sm:px-6 pt-4">
        <button
          onClick={fetchCourse}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 bg-[#009fe3]/10 hover:bg-[#009fe3]/20 text-[#009fe3] rounded-lg transition-colors disabled:opacity-50"
        >
          <FaSync className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="bg-white shadow-lg rounded-xl p-6 sm:p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
              <p className="font-semibold">Error</p>
              <p>{error}</p>
            </div>
          )}

          {/* Basic Info */}
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Basic Information
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Course Title <span className="text-red-500">*</span>
                </label>
                <input
                  value={course.title}
                  onChange={(e) => updateField("title", e.target.value)}
                  placeholder="Course Title"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  style={{ fontSize: "16px" }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={course.description}
                  onChange={(e) => updateField("description", e.target.value)}
                  placeholder="Course description..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  style={{ fontSize: "16px" }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Regulations
                </label>
                <textarea
                  value={course.regulations}
                  onChange={(e) => updateField("regulations", e.target.value)}
                  placeholder="Course regulations..."
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  style={{ fontSize: "16px" }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image URL
                </label>
                <input
                  value={course.imageUrl}
                  onChange={(e) => updateField("imageUrl", e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  style={{ fontSize: "16px" }}
                />
              </div>
            </div>
          </section>

          {/* Pricing */}
          <section className="mb-8 border-t pt-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Pricing Information
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  One-time Price (BDT)
                </label>
                <input
                  type="number"
                  value={course.price}
                  onChange={(e) => updateField("price", +e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  style={{ fontSize: "16px" }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Installment Count (Optional)
                </label>
                <input
                  type="number"
                  value={course.installmentCount || ""}
                  onChange={(e) =>
                    updateField(
                      "installmentCount",
                      +e.target.value || undefined
                    )
                  }
                  placeholder="e.g. 6"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  style={{ fontSize: "16px" }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Info
                </label>
                <input
                  value={course.paymentInfo || ""}
                  onChange={(e) =>
                    updateField("paymentInfo", e.target.value || undefined)
                  }
                  placeholder="e.g. Pay in 6 monthly installments"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  style={{ fontSize: "16px" }}
                />
              </div>

              {/* Price Options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price Options (German Levels)
                </label>
                <div className="space-y-2">
                  {course.priceOptions.map((opt, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input
                        value={opt.level}
                        onChange={(e) =>
                          updatePriceOpt(i, { level: e.target.value })
                        }
                        placeholder="Level (A1, A2, B1...)"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                        style={{ fontSize: "16px" }}
                      />
                      <input
                        type="number"
                        value={opt.amount}
                        onChange={(e) =>
                          updatePriceOpt(i, { amount: +e.target.value })
                        }
                        placeholder="Amount (BDT)"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                        style={{ fontSize: "16px" }}
                      />
                      <button
                        onClick={() => removePriceOpt(i)}
                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={addPriceOpt}
                  className="mt-2 text-[#009fe3] hover:text-[#007cb5] font-medium text-sm flex items-center gap-1"
                >
                  <FaPlus className="text-xs" />
                  Add Price Option
                </button>
              </div>
            </div>
          </section>

          {/* Optional Dates */}
          <section className="mb-8 border-t pt-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Course Dates (Optional)
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={course.startDate || ""}
                  onChange={(e) =>
                    updateField("startDate", e.target.value || undefined)
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  style={{ fontSize: "16px" }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Registration Deadline
                </label>
                <input
                  type="date"
                  value={course.registrationDeadline || ""}
                  onChange={(e) =>
                    updateField(
                      "registrationDeadline",
                      e.target.value || undefined
                    )
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  style={{ fontSize: "16px" }}
                />
              </div>
            </div>
          </section>

          {/* Outline Steps */}
          <section className="mb-8 border-t pt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">
                Course Outline Steps
              </h2>
              <button
                onClick={addStep}
                className="text-[#009fe3] hover:text-[#007cb5] font-medium text-sm flex items-center gap-1"
              >
                <FaPlus className="text-xs" />
                Add Step
              </button>
            </div>
            <div className="space-y-3">
              {course.outlineSteps.map((s, i) => (
                <div
                  key={i}
                  className="flex gap-2 items-start bg-gray-50 p-3 rounded-lg"
                >
                  <input
                    type="number"
                    value={s.stepOrder}
                    onChange={(e) =>
                      updateStep(i, { stepOrder: +e.target.value })
                    }
                    className="w-16 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                    style={{ fontSize: "16px" }}
                    placeholder="Order"
                  />
                  <input
                    value={s.title}
                    onChange={(e) => updateStep(i, { title: e.target.value })}
                    placeholder="Step Title"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                    style={{ fontSize: "16px" }}
                  />
                  <input
                    value={s.description}
                    onChange={(e) =>
                      updateStep(i, { description: e.target.value })
                    }
                    placeholder="Description"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                    style={{ fontSize: "16px" }}
                  />
                  <button
                    onClick={() => removeStep(i)}
                    className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                    title="Remove"
                  >
                    <FaTimes />
                  </button>
                </div>
              ))}
            </div>
          </section>

          <button
            onClick={handleSubmit}
            className="w-full bg-[#009fe3] text-white py-3 rounded-lg hover:bg-[#007cb5] transition-colors font-semibold shadow-md hover:shadow-lg"
          >
            Save Changes
          </button>
        </div>
      </main>
    </div>
  );
};

export default EditCourse;
